(ns hai.tasks.drawer.share-bar
  "The Share tab's search. It searches contacts as you type and takes a typed
   address or email domain; what is picked waits in the field, and Add adds
   all of it at the role beside it. At the foot of the open Share drawer it is
   always there; out on its own, closing it with anyone still waiting asks
   first."
  (:require
   [clojure.string :as str]
   [hai.contacts.helpers :as contacts]
   [hai.entity.form :as entity-form]
   [hai.entity.related :as related]
   [hai.sharing.components :as sharing.components]
   [hai.sharing.people :as people]
   [hai.sharing.recipients :as recipients]
   [hai.tasks.drawer.data :as data]
   [hai.tasks.drawer.parts :as parts]
   [hai.tasks.drawer.share :as share]
   [hai.tasks.urgency-menu :as urgency-menu]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e >e!]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private caret-icon (icons/mui-icon :outlined :expand_more))
(def ^:private add-icon (icons/mui-icon :outlined :check))

(def ^:private result-limit 5)

;;
;; Styles
;;

(o/defstyled share-field parts/field-frame
  {:flex-wrap "wrap" :row-gap "4px"}
  [:.field-input {:flex "1 1 90px"}]
  [:.field-lead
   {:display "inline-flex"
    :margin-left "2px"
    :color t/--link-muted}
   [:svg {:width "15px" :height "15px" :fill "currentColor"}]]
  [:.field-clear
   {:flex "none"
    :width "24px" :height "24px" :padding "0"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :border "none" :border-radius "7px"
    :background "transparent"
    :color t/--link-muted
    :cursor "pointer"}
   [:&:hover {:background t/--card-forest-06 :color t/--card-forest}]
   [:svg {:width "13px" :height "13px" :fill "currentColor"}]])

;; Told apart by shape: a contact is a pill, a domain squared off, an address
;; nobody holds a contact for dashed.
(o/defstyled token :span
  {:display "inline-flex" :align-items "center" :gap "4px"
   :box-sizing "border-box"
   :min-width "0" :max-width "100%"
   :height "24px" :padding "0 3px 0 9px"
   :border-width "1px" :border-style "solid" :border-color t/--property-3
   :border-radius "999px"
   :background t/--card-surface
   :font-size "12px" :font-weight "700"
   :color t/--card-forest}
  [:.token-glyph
   {:display "inline-flex"}
   [:svg {:width "12px" :height "12px" :fill "currentColor"}]]
  [:.token-label
   {:overflow "hidden" :text-overflow "ellipsis" :white-space "nowrap"}]
  [:.token-remove
   {:flex "none"
    :width "18px" :height "18px" :padding "0"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :border "none" :border-radius "50%"
    :background "transparent"
    :color t/--card-forest-70
    :cursor "pointer"}
   [:&:hover {:background t/--card-forest-10 :color t/--card-forest}]
   [:svg {:width "11px" :height "11px" :fill "currentColor"}]]
  [:&.domain {:border-radius "6px"}]
  [:&.email
   {:border-style "dashed"
    :background "transparent"
    :font-weight "600"}])

;; Add and the role those added join at, as the two halves of one control.
(o/defstyled add-control :span
  {:flex "none"
   :display "inline-flex" :align-items "stretch"
   :height "24px"
   :border-radius "7px"
   :overflow "hidden"
   :background t/--humble-primary-dark}
  [#{:.add-commit :.add-role}
   {:display "inline-flex" :align-items "center"
    :border "none"
    :background "transparent"
    :color "white"
    :cursor "pointer"}
   [:&:hover {:background t/--humble-primary}]
   [:svg {:width "13px" :height "13px" :fill "currentColor"}]]
  [:.add-commit
   {:gap "4px"
    :padding "0 9px"
    :font-family "inherit" :font-size "10.5px" :font-weight "800"
    :letter-spacing "0.04em" :text-transform "uppercase"}]
  [:.add-role
   {:gap "1px"
    :padding "0 4px 0 6px"
    :box-shadow "inset 1px 0 0 rgba(255,255,255,0.28)"}])

(o/defstyled share-result-row parts/result-row
  {:grid-template-columns "26px minmax(0, 1fr) auto"}
  [:.result-chip
   {:padding "2px 7px"
    :border-radius "6px"
    :background t/--card-forest-06
    :font-size "11px" :font-weight "700"
    :white-space "nowrap"
    :color t/--card-forest-70}]
  [:&.unavailable
   {:cursor "not-allowed"}
   [:.result-text {:opacity "0.5"}]])

(defn- focus-input! [!input]
  (some-> @!input .focus))

(defn- field-in
  "The field of the search on `drawer-key`, in `root`."
  [^js root drawer-key]
  (some-> root (.querySelector (str "[data-drawer-share-field=\""
                                    (js/CSS.escape (pr-str drawer-key))
                                    "\"]"))))

(defn focus-field!
  "Gives the caret to the field of the search on `drawer-key`, in `root` — the
   document or the shadow root the press was in: at once where it is there,
   since a touch keyboard rises only for the press itself, else once the press
   has brought it."
  [^js root drawer-key]
  (if-let [^js field (field-in root drawer-key)]
    (.focus field)
    (r/after-render #(some-> ^js (field-in root drawer-key) .focus))))

(defn- recipient->share
  [{:keys [kind contact email domain]}]
  (case kind
    :contact (sharing.components/->share contact)
    :email {:contact/email [email]}
    :domain {:entity/scope domain}))

(defn- staged-shares
  "Those `to-share`, as the shares Add stages, joining at `permission`."
  [to-share permission]
  (mapv #(people/staged-share % (recipient->share %) permission) to-share))

;;
;; Views
;;

(defn- recipient-label
  [{:keys [kind contact email domain]}]
  (case kind
    :contact (contacts/->label contact)
    :email email
    :domain (str "@" domain)))

(defn- row-display
  "How a suggested row reads: its label over an optional line of detail, and
   its glyph on a tile of `tint`."
  [{:keys [holder-key] {:keys [kind contact] :as recipient} :recipient}]
  (let [label (recipient-label recipient)]
    (case kind
      :contact {:label label
                :detail (when (:contact/full-name contact)
                          (first (:contact/email contact)))
                :tint "contact"
                :glyph (contacts/type-icon contact)}
      :email {:label label
              :tint "contact"
              :glyph icons/outlined-person}
      :domain {:label (if holder-key
                        label
                        (str "Add anyone from " label))
               :tint "link"
               :glyph icons/outlined-public})))

(defn- result
  [{:keys [row live? on-choose on-hover]}]
  (let [{:keys [label detail tint glyph]} (row-display row)
        {:keys [holder-key unavailable]} row]
    [share-result-row
     {:type "button"
      :class (str (when live? "on") (when unavailable " unavailable"))
      :title unavailable
      :aria-disabled (some? unavailable)
      ;; Picked on mouse-down so the field keeps the caret.
      :on-mouse-down (fn [e]
                       (.preventDefault e)
                       (on-choose row))
      :on-mouse-enter on-hover}
     [related/tile {:class tint} [glyph]]
     [:span.result-text
      [:span.result-label label]
      (when detail
        [:span.result-sub detail])]
     (when holder-key
       [:span.result-chip "Has access"])]))

(defn- results
  "What was found for what is typed, best nearest the field; `index` is the
   live row, counted from there. With no rows, `notice` says why. Its ✕
   closes the search."
  [{:keys [rows notice index on-choose on-hover on-close]}]
  [parts/results-band
   [:div.results-top
    [:div.results-head "Contacts"]
    [:button.results-close
     {:type "button"
      :aria-label "Close the search"
      :title "Close the search"
      :on-mouse-down #(.preventDefault %)
      :on-click on-close}
     [icons/outlined-close]]]
   (if (seq rows)
     (into [:div.results-rows]
           (for [[row-index row] (reverse (map-indexed vector rows))]
             ^{:key (get-in row [:recipient :key])}
             [result {:row row
                      :live? (= row-index index)
                      :on-choose on-choose
                      :on-hover #(on-hover row-index)}]))
     [:div.results-quiet notice])])

(defn- picked-token
  [{:keys [recipient on-remove]}]
  (let [label (recipient-label recipient)
        {:keys [kind]} recipient]
    [token {:class (name kind)
            :title (when (= :domain kind)
                     (str "Anyone with an " label " address"))}
     (when (= :domain kind)
       [:span.token-glyph [icons/outlined-public]])
     [:span.token-label label]
     [:button.token-remove
      {:type "button"
       :aria-label (str "Remove " label)
       :on-mouse-down #(.preventDefault %)
       :on-click on-remove}
      [icons/outlined-close]]]))

(defn- role-picker
  "The role those added join at, as the button opening its menu.
   `trigger-class` styles the button; `on-close` gets the focus back after the
   menu."
  [{:keys [permission trigger-class on-change on-close]}]
  (let [{:keys [icon]} (some #(when (= permission (:permission %)) %) share/share-roles)
        hint (str "They join as " (sharing.components/permission-label permission))]
    ;; Non-modal, as the urgency menu is: a modal one locks the page's scroll,
    ;; the host page's in the extension.
    [c/DropdownMenuRoot {:modal false}
     [c/DropdownMenuTrigger
      [:button {:type "button"
                :class trigger-class
                :title hint
                :aria-label hint}
       [icon]
       [caret-icon]]]
     [c/DropdownMenuContent
      {:align "end"
       :class (str urgency-menu/menu-content)
       :onFocusOutside (fn [^js e] (.preventDefault e))
       :onCloseAutoFocus (fn [^js e]
                           (.preventDefault e)
                           (on-close))}
      [c/DropdownMenuLabel "They join as"]
      [share/role-items {:permission permission
                         :on-change on-change}]]]))

(defn- on-key-down
  [{:keys [drawer-key search rows index listing? on-choose on-share on-close]} ^js e]
  (let [{:keys [text picked list-open?]} search]
    (case (.-key e)
      ;; The list grows away from the field, so ↑ walks away from it.
      "ArrowUp" (when (and listing? (seq rows))
                  (.preventDefault e)
                  (>e [::data/point drawer-key :share-search (min (inc index) (dec (count rows)))]))
      "ArrowDown" (when (and listing? (seq rows))
                    (.preventDefault e)
                    (>e [::data/point drawer-key :share-search (max 0 (dec index))]))
      "Enter" (do (.preventDefault e)
                  (cond
                    (or (.-metaKey e) (.-ctrlKey e)) (on-share)
                    (and listing? (seq rows)) (on-choose (nth rows index))))
      "Backspace" (when (and (= "" text) (seq picked))
                    (.preventDefault e)
                    (>e [::data/unpick-last drawer-key]))
      ;; The first closes the list, the next clears what is typed, and the one
      ;; after that closes the search, or, where it stays, leaves the field
      ;; with anyone picked still in it.
      "Escape" (do (.preventDefault e)
                   (.stopPropagation e)
                   (cond
                     list-open? (>e [::data/close-list drawer-key :share-search])
                     (seq text) (>e [::data/clear-text drawer-key :share-search])
                     on-close (on-close)
                     :else (.. e -target blur)))
      nil)))

(defn- suggested
  "What the bar offers for what is typed, against those `listed`, each row
   `:unavailable` with the reason while it can't be picked."
  [listed {:keys [text picked found]}]
  (let [fresh? (= (:text found) (str/trim text))
        {:keys [rows notice]} (recipients/suggestions {:text text
                                                       :contacts (when fresh? (:contacts found))
                                                       :people listed
                                                       :picked picked
                                                       :limit result-limit})]
    {:fresh? fresh?
     :notice notice
     :rows (mapv (fn [{:keys [recipient] :as row}]
                   (assoc row :unavailable (sharing.components/contact-disabled-reason (:contact recipient))))
                 rows)}))

(defn- search-field
  "The field and, above it, what was found for what is typed
   ([[component]]'s props, with the `search` they show)."
  [{:keys [drawer-key search listed on-add on-reveal on-close]}]
  (r/with-let [!input (atom nil)]
    (let [{:keys [text picked permission found list-open?]} search
          {:keys [rows notice fresh?]} (suggested listed {:text text :picked picked :found found})
          index (max 0 (min (:index search) (dec (count rows))))
          listing? (and list-open? fresh? (or (seq rows) (some? notice)))
          ;; A typed address or domain not picked yet goes with the rest.
          typed (some (fn [{:keys [recipient holder-key]}]
                        (when (and (not holder-key) (not= :contact (:kind recipient)))
                          recipient))
                      rows)
          to-share (cond-> picked typed (conj typed))
          focus! #(focus-input! !input)
          ;; Someone already sharing it is shown where they are listed; anyone
          ;; else waits picked in the field.
          choose-row! (fn [{:keys [holder-key unavailable] :as row}]
                        (when-not unavailable
                          (>e [::data/choose drawer-key row])
                          (when holder-key
                            (on-reveal holder-key))
                          (focus!)))
          share-all! (fn []
                       (when (seq to-share)
                         (on-add (staged-shares to-share permission))
                         (>e [::data/clear drawer-key])
                         (focus!)))
          ;; With anyone picked and not added, it asks first. Where the search
          ;; stays, there is nothing to close.
          close-search! (when on-close
                          #(if (seq picked)
                             (>e [::data/ask-to-close-share drawer-key])
                             (on-close)))
          close-list! #(>e [::data/close-list drawer-key :share-search])
          set-permission! #(>e [::data/set-permission drawer-key %])]
      ;; Keyed, so the field stays the same element — and keeps the caret — as
      ;; the list mounts above it and unmounts.
      [:<>
       (when listing?
         ^{:key "results"}
         [results {:rows rows
                   :notice notice
                   :index index
                   :on-choose choose-row!
                   :on-hover #(>e [::data/point drawer-key :share-search %])
                   :on-close (or close-search! close-list!)}])
       ^{:key "field"}
       [share-field
        {:on-click (fn [e]
                     (when (= (.-target e) (.-currentTarget e))
                       (focus!)))}
        (when (and (empty? picked) (= "" text))
          [:span.field-lead [icons/outlined-search]])
        (for [recipient picked]
          ^{:key (:key recipient)}
          [picked-token {:recipient recipient
                         :on-remove (fn []
                                      (>e [::data/unpick drawer-key (:key recipient)])
                                      (focus!))}])
        [:input.field-input
         {:ref #(reset! !input %)
          :value text
          :placeholder (when (empty? picked) "Search or add contacts")
          :aria-label "Share with: search contacts, or type an email address or a domain"
          ;; Found by [[focus-field!]], for the header's Share to give it the
          ;; caret.
          :data-drawer-share-field (pr-str drawer-key)
          :auto-complete "off"
          :spell-check false
          ;; At once: a render ahead of a queued keystroke would put older text
          ;; back in the box.
          :on-change #(>e! [::data/type-text drawer-key :share-search (.. % -target -value) nil])
          ;; A press on the field shows or hides what was found for it.
          :on-click #(>e [::data/press-field drawer-key :share-search])
          :on-blur #(>e [::data/leave-field drawer-key :share-search])
          :on-key-down #(on-key-down {:drawer-key drawer-key
                                      :search search
                                      :rows rows
                                      :index index
                                      :listing? listing?
                                      :on-choose choose-row!
                                      :on-share share-all!
                                      :on-close close-search!}
                                     %)}]
        (when (or (seq picked) (not= "" text))
          [:button.field-clear
           {:type "button"
            :aria-label "Clear"
            :title "Clear"
            :on-mouse-down #(.preventDefault %)
            :on-click (fn []
                        (>e [::data/clear drawer-key])
                        (focus!))}
           [icons/outlined-close]])
        (if (seq to-share)
          [add-control
           [:button.add-commit
            {:type "button"
             :title "Share with them (⌘/Ctrl+↵)"
             :on-mouse-down #(.preventDefault %)
             :on-click share-all!}
            [add-icon]
            [:span (str "Add " (count to-share))]]
           [role-picker {:permission permission
                         :trigger-class "add-role"
                         :on-change set-permission!
                         :on-close focus!}]]
          [role-picker {:permission permission
                        :trigger-class (str parts/menu-button)
                        :on-change set-permission!
                        :on-close focus!}])]])))

(defn- discard-question
  "Asks whether to close the search on the `picked-count` picked and not yet
   added."
  [{:keys [picked-count on-keep on-discard]}]
  [entity-form/footer
   [entity-form/confirm
    {:prompt (str "Discard "
                  (if (= 1 picked-count)
                    "the person"
                    (str "the " picked-count " people"))
                  " you picked? They have not been added yet.")
     :dismiss-label "Keep editing"
     :confirm-label "Discard"
     :destructive? true
     :on-dismiss on-keep
     :on-confirm on-discard}]])

(defn component
  "The search adding to those `listed`, the Share tab's rows, its state kept
   under `drawer-key` ([[hai.tasks.drawer.data]]): `on-add` gets what Add
   stages, each a share at the role picked, and `on-reveal` the key of the row
   of someone picked who is listed already. `on-close`, where the search can
   be put away, does so; asked to close with anyone picked and not added, it
   asks first, and Discard leaves it as it opened. It doesn't take the caret
   as it mounts — [[focus-field!]] gives it."
  [{:keys [drawer-key on-close] :as props}]
  (let [{:keys [picked confirming?] :as search} @(<s [::data/search drawer-key :share-search])]
    (if confirming?
      [discard-question {:picked-count (count picked)
                         :on-keep #(>e [::data/keep-editing drawer-key])
                         :on-discard (fn []
                                       (>e [::data/reset-search drawer-key :share-search])
                                       (on-close))}]
      [search-field (assoc props :search search)])))
